import React from 'react';

import { domHooks } from '@nxte-nl/dom-hooks/src/domHooks';

import { GlobalMessages, GlobalSignals } from '../../types/Messages';
import { ModalTypes, OnModalOpen } from '../../types/Modals';
import { messageHooks } from '../messageHooks/messageHooks';

export type UseModalResult = {
  isOpenModal: () => boolean;
  onModalClose: () => void;
  onModalOpen: OnModalOpen;
  openModals: ModalTypes[];
};

export const modalHooks = {
  useDisableScrolling() {
    const [disableScrolling, setDisableScrolling] = React.useState<boolean>(false);
    domHooks.useDisableScrolling(disableScrolling, {
      preserveScrollPosition: true,
    });

    messageHooks.useSubscribeSignal(GlobalSignals.OnChangeModals, (modals) => {
      setDisableScrolling(modals.length > 0);
    });
  },

  useModal(modal: ModalTypes): UseModalResult {
    const openModals = messageHooks.useSubjectState(GlobalMessages.OpenModals);
    const setOpenModals = messageHooks.useNext(GlobalMessages.OpenModals);
    const onChangeModalsSignal = messageHooks.useNextSignal(GlobalSignals.OnChangeModals);

    const onModalOpen: OnModalOpen = React.useCallback(
      (closePrevModals = true) => {
        const modals = closePrevModals ? [modal] : [...openModals, modal];
        setOpenModals(modals);
        onChangeModalsSignal(modals);
      },
      [modal, openModals, setOpenModals, onChangeModalsSignal],
    );

    const onModalClose = React.useCallback(() => {
      const modals = openModals.filter(modalType => modalType !== modal);
      setOpenModals(modals);
      onChangeModalsSignal(modals);
    },
    [modal, setOpenModals, onChangeModalsSignal, openModals]);

    const isOpenModal = React.useCallback(
      () => openModals.includes(modal),
      [modal, openModals],
    );

    return {
      isOpenModal,
      openModals,
      onModalClose,
      onModalOpen,
    };
  },

  useCloseAllModals() {
    const setOpenModals = messageHooks.useNext(GlobalMessages.OpenModals);
    const onChangeModalsSignal = messageHooks.useNextSignal(GlobalSignals.OnChangeModals);
    const handleCloseAllModals = React.useCallback(() => {
      setOpenModals([]);
      onChangeModalsSignal([]);
    }, [onChangeModalsSignal, setOpenModals]);

    return { onCloseAllModals: handleCloseAllModals };
  },
  useOnModalOpenWithoutCloseOtherModals(onModalOpen: OnModalOpen) {
    return React.useCallback(() => {
      onModalOpen(false);
    }, [onModalOpen]);
  },
};
